import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Upload as UploadIcon,
  X as XIcon,
  Calculator,
  ChevronDown,
  ChevronUp,
  Plus,
  Trash2,
} from "lucide-react";
import FormInput from "../../../../Team/AddNutritionist/components/FormInput";
import FormDropdown from "../../../../Team/AddNutritionist/components/FormDropdown";
import CustomDropdown, {
  DropdownOption,
} from "../../../../Ui_Components/CustomDropdown";
import { Button } from "../../../../Ui_Components/Button";
import {
  CookingFatBand,
  DetailsNutritionData,
  DetailsNutritionErrors,
  MicroNutrientKey,
  RecipeIngredientRow,
  createEmptyCookingFatBand,
  createEmptyIngredientRow,
  initialMicroNutrients,
} from "../types";
import { checkDishNameExists } from "../../../../../services/dishes";
import { getIngredientCategories } from "../../../../../services/ingredients";
import {
  IngredientAutocomplete,
  IngredientSuggestion,
  computeRowNutrition,
  formatCell,
} from "../DishCalculatorModal";
import {
  calculateCPFRatio,
  formatCPFRatio,
} from "../../../../Plans/Meal_Creation/CPFCalculator";

export interface DuplicateNameInfo {
  matchedItemName: string;
  matchedDishId: number;
}

interface DetailsNutritionStepProps {
  data: DetailsNutritionData;
  onChange: (data: DetailsNutritionData) => void;
  errors?: DetailsNutritionErrors;
  onError?: (title: string, message?: string) => void;
  onOpenDishCalculator?: () => void;
  // In edit mode the current record's own name is not a duplicate — the
  // parent passes it so the check can skip identical values.
  originalName?: string;
  // In edit mode the current record's dish_id is excluded from the check
  // result, so editing a dish without renaming it doesn't flag itself.
  currentDishId?: number;
  // Bubbled up so the parent can block "Save & next" while a duplicate is
  // active. Cleared (null) when the name is empty, matches the original,
  // or the check passes.
  onDuplicateChange?: (info: DuplicateNameInfo | null) => void;
}

const MAX_FILE_SIZE_BYTES = 1 * 1024 * 1024; // 1 MB per mockup

const unitOptions: DropdownOption[] = [
  { value: "gm", label: "gm" },
  { value: "ml", label: "ml" },
];

const typeOptions: DropdownOption[] = [
  { value: "veg", label: "Veg" },
  { value: "nonveg", label: "Non-veg" },
];

const numeric = (v: string) => v.replace(/[^0-9.]/g, "");

// Display labels for the 29 micro-nutrient inputs. Order mirrors the mockup
// (fat-soluble → water-soluble vitamins → macro minerals → trace minerals)
// and is intentionally the same as the ingredient step.
const MICRO_NUTRIENT_FIELD_DEFS: Array<{
  key: MicroNutrientKey;
  label: string;
}> = [
  { key: "vitaminA", label: "Vitamin A" },
  { key: "vitaminD", label: "Vitamin D" },
  { key: "vitaminE", label: "Vitamin E" },
  { key: "vitaminK", label: "Vitamin K" },
  { key: "vitaminC", label: "Vitamin C" },
  { key: "thiamine", label: "Thiamine (Vitamin B1)" },
  { key: "riboflavin", label: "Riboflavin (Vitamin B2)" },
  { key: "niacin", label: "Niacin (Vitamin B3)" },
  { key: "pantothenicAcid", label: "Pantothenic Acid (Vitamin B5)" },
  { key: "vitaminB6", label: "Vitamin B6" },
  { key: "biotin", label: "Biotin (Vitamin B7)" },
  { key: "folate", label: "Folate (Vitamin B9)" },
  { key: "vitaminB12", label: "Vitamin B12" },
  { key: "choline", label: "Choline" },
  { key: "calcium", label: "Calcium" },
  { key: "phosphorus", label: "Phosphorus" },
  { key: "magnesium", label: "Magnesium" },
  { key: "sodium", label: "Sodium" },
  { key: "potassium", label: "Potassium" },
  { key: "chloride", label: "Chloride" },
  { key: "iron", label: "Iron" },
  { key: "zinc", label: "Zinc" },
  { key: "copper", label: "Copper" },
  { key: "manganese", label: "Manganese" },
  { key: "selenium", label: "Selenium" },
  { key: "iodine", label: "Iodine" },
  { key: "chromium", label: "Chromium" },
  { key: "molybdenum", label: "Molybdenum" },
  { key: "fluoride", label: "Fluoride" },
];

const DetailsNutritionStep: React.FC<DetailsNutritionStepProps> = ({
  data,
  onChange,
  errors = {},
  onError,
  onOpenDishCalculator,
  originalName,
  currentDishId,
  onDuplicateChange,
}) => {
  const navigate = useNavigate();
  const photoInputRef = useRef<HTMLInputElement>(null);
  const [tagDraft, setTagDraft] = useState("");
  // Micro nutrients panel starts collapsed to match the mockup — user opens
  // it explicitly when they want to fill values.
  const [microOpen, setMicroOpen] = useState(false);

  // Duplicate-name state for the inline check. `null` means no duplicate;
  // otherwise carries what to display in the error and where the "View"
  // link should navigate.
  const [duplicateInfo, setDuplicateInfo] = useState<DuplicateNameInfo | null>(
    null,
  );
  const [checkingName, setCheckingName] = useState(false);

  // Category dropdown is backed by the same admin master-list API the
  // ingredient flow uses — any additions on the admin side show up here
  // without a code change. Fetched once on mount; the payload is small.
  const [categoryOptions, setCategoryOptions] = useState<DropdownOption[]>([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const categories = await getIngredientCategories();
        if (cancelled) return;
        setCategoryOptions(
          categories.map((c) => ({
            value: c.category_name,
            label: c.category_name,
          })),
        );
      } catch (err) {
        if (cancelled) return;
        console.error("[DishDetailsNutritionStep] categories fetch failed", err);
        onError?.(
          "Could not load category options",
          err instanceof Error ? err.message : undefined,
        );
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* Debounced duplicate-name check. Fires CHECK_DISH_NAME_EXISTS once the
     user has stopped typing for 500ms. Skipped when the name is empty or
     (in edit mode) matches the record's original name. A response that
     points back at the current record (edit mode) is also ignored —
     editing a dish without renaming it shouldn't flag itself. */
  useEffect(() => {
    const trimmed = data.name.trim();
    const isUnchanged =
      typeof originalName === "string" &&
      trimmed.toLowerCase() === originalName.trim().toLowerCase();
    if (!trimmed || isUnchanged) {
      setDuplicateInfo(null);
      setCheckingName(false);
      onDuplicateChange?.(null);
      return;
    }
    let cancelled = false;
    setCheckingName(true);
    const timer = setTimeout(async () => {
      try {
        const api = await checkDishNameExists(trimmed);
        if (cancelled) return;
        const isSuccess =
          api?.success === true ||
          api?.success === 1 ||
          api?.success === "1" ||
          api?.success === "true";
        const matchedDishId =
          typeof api?.matched_dish_id === "number"
            ? api.matched_dish_id
            : Number(api?.matched_dish_id);
        const isSelfMatch =
          typeof currentDishId === "number" &&
          Number.isFinite(matchedDishId) &&
          matchedDishId === currentDishId;
        if (isSuccess && api?.exists && !isSelfMatch && matchedDishId) {
          const info: DuplicateNameInfo = {
            matchedItemName: api.matched_dish_name || trimmed,
            matchedDishId,
          };
          setDuplicateInfo(info);
          onDuplicateChange?.(info);
        } else {
          setDuplicateInfo(null);
          onDuplicateChange?.(null);
        }
      } catch (err) {
        if (cancelled) return;
        // Silent on failure — don't block the form on a network hiccup.
        console.error("[DishDetailsNutritionStep] name check failed", err);
        setDuplicateInfo(null);
        onDuplicateChange?.(null);
      } finally {
        if (!cancelled) setCheckingName(false);
      }
    }, 500);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.name, originalName, currentDishId]);
  const [confirmDeleteIngredientId, setConfirmDeleteIngredientId] = useState<
    string | null
  >(null);
  const [confirmDeleteBandId, setConfirmDeleteBandId] = useState<string | null>(
    null,
  );

  const update = (patch: Partial<DetailsNutritionData>) =>
    onChange({ ...data, ...patch });

  const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_FILE_SIZE_BYTES) {
      onError?.("File too large", "Dish photo must be below 1 MB.");
      e.target.value = "";
      return;
    }
    const preview = URL.createObjectURL(file);
    update({ photo: file, photoPreview: preview });
  };

  const tags = data.tags ?? [];
  const micro = data.micro ?? initialMicroNutrients;
  const recipeIngredients =
    data.recipeIngredients && data.recipeIngredients.length > 0
      ? data.recipeIngredients
      : [createEmptyIngredientRow()];
  const cookingFatBands =
    data.cookingFatBands && data.cookingFatBands.length > 0
      ? data.cookingFatBands
      : [createEmptyCookingFatBand()];

  const commitTagDraft = () => {
    const next = tagDraft.trim();
    if (!next) return;
    if (!tags.includes(next)) update({ tags: [...tags, next] });
    setTagDraft("");
  };

  const handleTagKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      commitTagDraft();
      return;
    }
    if (e.key === "Backspace" && tagDraft.length === 0 && tags.length > 0) {
      update({ tags: tags.slice(0, -1) });
    }
  };

  const handleTagChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value;
    if (v.includes(",")) {
      const parts = v.split(",").map((p) => p.trim()).filter(Boolean);
      const merged = [...tags];
      parts.forEach((p) => {
        if (!merged.includes(p)) merged.push(p);
      });
      update({ tags: merged });
      setTagDraft("");
      return;
    }
    setTagDraft(v);
  };

  const removeTag = (tag: string) =>
    update({ tags: tags.filter((t) => t !== tag) });

  /* ── Recipe ingredients ───────────────────────────────────── */

  /* Re-derive the nutrition cells + CPF
     from the row's ingredient baseline + quantity + unit. Called from the
     autocomplete select and the quantity / unit inputs so live edits stay
     in sync with the ingredient's per-100g values. */
  const patchIngredientWithNutrition = (
    id: string,
    patch: Partial<RecipeIngredientRow>,
  ) => {
    update({
      recipeIngredients: recipeIngredients.map((r) => {
        if (r.id !== id) return r;
        const merged: RecipeIngredientRow = { ...r, ...patch };
        const n = computeRowNutrition({
          quantity: merged.quantity,
          unit: merged.unit,
          measurements: merged.measurements,
          baseQuantity: merged.baseQuantity,
          nutritionPer100g: merged.nutritionPer100g,
        });
        const cpf = formatCPFRatio(
          calculateCPFRatio(n.carb, n.protein, n.fat, n.cal).ratio,
        );
        return {
          ...merged,
          cal: formatCell(n.cal),
          aProt: formatCell(n.protein),
          grossProtein: formatCell(n.grossProtein),
          fats: formatCell(n.fat),
          carb: formatCell(n.carb),
          netCarb: formatCell(n.netCarb),
          gl: formatCell(n.gl),
          cpf,
        };
      }),
    });
  };

  // Pick the ingredient's default unit if it maps to a real measurement;
  // otherwise fall back to gm/ml or the first measurement so the row is
  // immediately usable and nutrition doesn't sit at zero.
  const resolveInitialUnit = (
    measurements: IngredientSuggestion["measurements"],
    defaultUnit: string,
  ): string => {
    const hasDefault = measurements.some((m) => m.unit_type === defaultUnit);
    if (hasDefault) return defaultUnit;
    return (
      measurements.find(
        (m) => m.unit_type === "gm" || m.unit_type === "ml",
      )?.unit_type ??
      measurements[0]?.unit_type ??
      ""
    );
  };

  const handleIngredientSelect = (
    rowId: string,
    suggestion: IngredientSuggestion,
  ) => {
    patchIngredientWithNutrition(rowId, {
      name: suggestion.name,
      imageUrl: suggestion.imageUrl,
      foodId: suggestion.foodId,
      baseQuantity: suggestion.baseQuantity,
      measurements: suggestion.measurements,
      nutritionPer100g: suggestion.nutritionPer100g,
      unit: resolveInitialUnit(suggestion.measurements, suggestion.defaultUnit),
    });
  };

  const handleIngredientClear = (rowId: string) => {
    const existing = recipeIngredients.find((r) => r.id === rowId);
    if (!existing) return;
    update({
      recipeIngredients: recipeIngredients.map((r) =>
        r.id === rowId
          ? { ...createEmptyIngredientRow(), id: r.id }
          : r,
      ),
    });
  };

  const isIngredientRowEmpty = (row: RecipeIngredientRow) =>
    !row.name && !row.quantity && !row.unit;

  const requestDeleteIngredient = (row: RecipeIngredientRow) => {
    // Preserve the "at least one row" invariant: an empty sole row has
    // nothing to delete, so just no-op instead of prompting.
    if (recipeIngredients.length === 1 && isIngredientRowEmpty(row)) return;
    setConfirmDeleteIngredientId(row.id);
  };

  const confirmDeleteIngredient = (id: string) => {
    const remaining = recipeIngredients.filter((r) => r.id !== id);
    update({
      recipeIngredients:
        remaining.length === 0 ? [createEmptyIngredientRow()] : remaining,
    });
    setConfirmDeleteIngredientId(null);
  };

  const addIngredientRow = () =>
    update({
      recipeIngredients: [...recipeIngredients, createEmptyIngredientRow()],
    });

  /* ── Cooking fat bands ────────────────────────────────────── */

  const patchBand = (id: string, patch: Partial<CookingFatBand>) => {
    update({
      cookingFatBands: cookingFatBands.map((b) =>
        b.id === id ? { ...b, ...patch } : b,
      ),
    });
  };

  const isBandEmpty = (b: CookingFatBand) => !b.minQty && !b.maxQty && !b.fat;

  const requestDeleteBand = (band: CookingFatBand) => {
    if (cookingFatBands.length === 1 && isBandEmpty(band)) return;
    setConfirmDeleteBandId(band.id);
  };

  const confirmDeleteBand = (id: string) => {
    const remaining = cookingFatBands.filter((b) => b.id !== id);
    update({
      cookingFatBands:
        remaining.length === 0 ? [createEmptyCookingFatBand()] : remaining,
    });
    setConfirmDeleteBandId(null);
  };

  const addBandRow = () =>
    update({
      cookingFatBands: [...cookingFatBands, createEmptyCookingFatBand()],
    });

  const toggleDynamicCookingFat = () => {
    const nextDynamic = !data.cookingFatDynamic;
    update({
      cookingFatDynamic: nextDynamic,
      cookingFatBands: nextDynamic
        ? cookingFatBands
        : [createEmptyCookingFatBand()],
    });
    setConfirmDeleteBandId(null);
  };

  const dynamic = data.cookingFatDynamic;

  return (
    <div className="pt-4 space-y-4">
      {/* Card 1: photo + name + unit/base qty/category/type */}
      <div className="bg-white rounded-md border border-[#F0F0F0] p-4 sm:p-6">
        <div className="grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-6">
          <div className="flex flex-col items-center">
            <button
              type="button"
              onClick={() => photoInputRef.current?.click()}
              className="w-full aspect-square border-2 border-dashed border-[#D1D5DB] rounded-md flex flex-col items-center justify-center gap-2 hover:border-[#0DB5B5] transition-colors overflow-hidden"
            >
              {data.photoPreview ? (
                <img
                  src={data.photoPreview}
                  alt="Dish"
                  className="w-full h-full object-cover"
                />
              ) : (
                <>
                  <div className="w-9 h-9 rounded-full bg-[#F3F4F6] flex items-center justify-center">
                    <UploadIcon className="w-4 h-4 text-[#6B7280]" />
                  </div>
                  <span className="text-xs 3xl:text-sm font-inter text-[#6B7280]">
                    Click to upload photo
                  </span>
                </>
              )}
            </button>
            <p className="mt-2 text-[11px] 3xl:text-xs font-inter text-[#9CA3AF]">
              JPG/PNG · Max 1 MB
            </p>
            <input
              ref={photoInputRef}
              type="file"
              accept="image/png,image/jpeg"
              className="hidden"
              onChange={handlePhotoChange}
            />
          </div>

          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-1.5">
              <label className="text-sm 3xl:text-base font-medium font-inter text-[#1F1F1F]">
                Dish name<span className="text-red-500 ml-0.5">*</span>
              </label>
              <input
                type="text"
                value={data.name}
                onChange={(e) => update({ name: e.target.value })}
                placeholder="eg. Paneer butter masala"
                className={`w-full px-3 py-2 border rounded-md text-sm 3xl:text-base font-inter text-[#1F1F1F] placeholder:text-[#9E9E9E] outline-none transition-colors bg-white ${
                  errors.name || duplicateInfo
                    ? "border-red-400 focus:border-red-500"
                    : "border-[#E0E0E0] focus:border-[#0DB5B5]"
                }`}
              />
              {errors.name && !duplicateInfo && (
                <p className="text-xs 3xl:text-sm text-red-500 font-inter">
                  {errors.name}
                </p>
              )}
              {duplicateInfo && (
                <p className="text-xs 3xl:text-sm font-inter">
                  <span className="text-red-500">
                    {duplicateInfo.matchedItemName} already exists.
                  </span>{" "}
                  <button
                    type="button"
                    onClick={() =>
                      navigate(
                        `/food-library/dish/${duplicateInfo.matchedDishId}`,
                      )
                    }
                    className="text-[#0DB5B5] hover:underline font-medium"
                  >
                    View
                  </button>
                </p>
              )}
              {!duplicateInfo && !errors.name && checkingName && (
                <p className="text-xs 3xl:text-sm text-[#9E9E9E] font-inter">
                  Checking...
                </p>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <FormDropdown
                label="Units"
                options={unitOptions}
                value={data.unit}
                onChange={(v) => update({ unit: v })}
                placeholder="eg. gm"
                error={errors.unit}
                required
              />
              <FormInput
                label="Base qty"
                value={data.baseQty}
                onChange={(v) => update({ baseQty: numeric(v) })}
                placeholder="eg. 100"
                error={errors.baseQty}
                required
              />
              <FormDropdown
                label="Category"
                options={categoryOptions}
                value={data.category}
                onChange={(v) => update({ category: v })}
                placeholder="eg. dairy"
                error={errors.category}
                required
              />
              <FormDropdown
                label="Type"
                options={typeOptions}
                value={data.type}
                onChange={(v) => update({ type: v })}
                placeholder="eg. veg"
                error={errors.type}
                required
              />
            </div>
          </div>
        </div>
      </div>

      {/* Card 2: nutrition macros */}
      <div className="bg-white rounded-md border border-[#F0F0F0] p-4 sm:p-6">
        <h3 className="text-sm 3xl:text-base font-semibold font-inter text-[#1F1F1F] mb-4">
          Macro nutrients
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <FormInput
            label="Calories"
            unit="kcals"
            value={data.calories}
            onChange={(v) => update({ calories: numeric(v) })}
            placeholder="eg . 2000"
            error={errors.calories}
            required
          />
          <FormInput
            label="Adj protein"
            unit="g"
            value={data.adjProtein}
            onChange={(v) => update({ adjProtein: numeric(v) })}
            placeholder="eg. 20"
            error={errors.adjProtein}
            required
          />
          <FormInput
            label="Gross protein"
            unit="g"
            value={data.grossProtein}
            onChange={(v) => update({ grossProtein: numeric(v) })}
            placeholder="eg . 10"
            error={errors.grossProtein}
            required
          />
          <FormInput
            label="Carbs"
            unit="g"
            value={data.carbs}
            onChange={(v) => update({ carbs: numeric(v) })}
            placeholder="eg. 100"
            error={errors.carbs}
          />
          <FormInput
            label="Net Carbs"
            unit="g"
            value={data.netCarbs}
            onChange={(v) => update({ netCarbs: numeric(v) })}
            placeholder="eg. 100"
            error={errors.netCarbs}
            required
          />
          <FormInput
            label="Fat"
            unit="g"
            value={data.fat}
            onChange={(v) => update({ fat: numeric(v) })}
            placeholder="eg. 5"
            error={errors.fat}
            required
          />
          <FormInput
            label="GL"
            value={data.gl}
            onChange={(v) => update({ gl: numeric(v) })}
            placeholder="eg. 0.2"
            error={errors.gl}
            required
          />
          <FormInput
            label="Fiber"
            unit="g"
            value={data.fiber}
            onChange={(v) => update({ fiber: numeric(v) })}
            placeholder="eg. 0.2"
            error={errors.fiber}
          />
        </div>
      </div>

      {/* Card 2b: Micro nutrients (collapsible) */}
      <div className="bg-white rounded-md border border-[#F0F0F0] p-4 sm:p-6">
        <button
          type="button"
          onClick={() => setMicroOpen((v) => !v)}
          className="w-full flex items-center justify-between"
          aria-expanded={microOpen}
        >
          <h3 className="text-sm 3xl:text-base font-semibold font-inter text-[#1F1F1F]">
            Micro nutrients
          </h3>
          {microOpen ? (
            <ChevronUp className="w-4 h-4 text-[#6B7280]" />
          ) : (
            <ChevronDown className="w-4 h-4 text-[#6B7280]" />
          )}
        </button>
        {microOpen && (
          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {MICRO_NUTRIENT_FIELD_DEFS.map((field) => (
              <FormInput
                key={field.key}
                label={field.label}
                value={micro[field.key]}
                onChange={(v) =>
                  update({
                    micro: { ...micro, [field.key]: numeric(v) },
                  })
                }
                placeholder="2000"
              />
            ))}
          </div>
        )}
      </div>

      {/* Card 3: Cooking fat (with optional dynamic bands) */}
      <div className="bg-white rounded-md border border-[#F0F0F0] p-4 sm:p-6">
        <div className="flex items-start justify-between gap-4">
          <div className="flex flex-col">
            <span className="text-sm 3xl:text-base font-semibold font-inter text-[#1F1F1F]">
              Cooking fat
            </span>
            <span className="text-xs 3xl:text-sm font-inter text-[#6B7280] mt-0.5">
              Absorbed oil scales with serving size, so it&apos;s set as
              quantity bands rather than one value.
            </span>
          </div>
          <label className="flex items-center gap-2 cursor-pointer select-none shrink-0">
            <span className="text-xs 3xl:text-sm font-medium font-inter text-[#1F1F1F]">
              Dynamic
            </span>
            <button
              type="button"
              role="switch"
              aria-checked={dynamic}
              onClick={toggleDynamicCookingFat}
              className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                dynamic ? "bg-[#0DB5B5]" : "bg-[#D1D5DB]"
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                  dynamic ? "translate-x-[18px]" : "translate-x-0.5"
                }`}
              />
            </button>
          </label>
        </div>

        {dynamic && (
          <div className="mt-5 w-full lg:w-3/4 xl:w-2/3 lg:max-w-[780px]">
            <div className="grid grid-cols-[1fr_1fr_auto_1fr_40px] items-center gap-3 mb-2">
              <span className="text-xs 3xl:text-sm font-medium font-inter text-[#1F1F1F]">
                Min qty (g)
              </span>
              <span className="text-xs 3xl:text-sm font-medium font-inter text-[#1F1F1F]">
                Max qty (g)
              </span>
              <span />
              <span className="text-xs 3xl:text-sm font-medium font-inter text-[#1F1F1F]">
                Fat (g)
              </span>
              <span />
            </div>

            <div className="flex flex-col gap-3">
              {cookingFatBands.map((band) => {
                if (confirmDeleteBandId === band.id) {
                  return (
                    <div
                      key={band.id}
                      className="flex items-center justify-between px-3 py-[6px] bg-[#FFF5F5] border border-[#FFCCC9] rounded-md"
                    >
                      <span className="text-sm text-[#1F1F1F] truncate mr-4">
                        Delete this quantity band?
                      </span>
                      <div className="flex items-center gap-2 shrink-0">
                        <Button
                          variant="danger"
                          size="xs"
                          onClick={() => confirmDeleteBand(band.id)}
                          className="whitespace-nowrap"
                        >
                          Confirm delete
                        </Button>
                        <button
                          type="button"
                          onClick={() => setConfirmDeleteBandId(null)}
                          className="text-[#8E8E93] hover:text-[#1F1F1F] transition-colors p-1 rounded border-[0.5px] border-[#1F1F1F]"
                        >
                          <XIcon className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  );
                }
                return (
                  <div
                    key={band.id}
                    className="grid grid-cols-[1fr_1fr_auto_1fr_40px] items-center gap-3"
                  >
                    <input
                      type="text"
                      inputMode="decimal"
                      value={band.minQty}
                      onChange={(e) =>
                        patchBand(band.id, { minQty: numeric(e.target.value) })
                      }
                      placeholder="0"
                      className="w-full px-3 py-2 border border-[#E0E0E0] rounded-md text-sm 3xl:text-base font-inter text-[#1F1F1F] outline-none transition-colors focus:border-[#0DB5B5]"
                    />
                    <input
                      type="text"
                      inputMode="decimal"
                      value={band.maxQty}
                      onChange={(e) =>
                        patchBand(band.id, { maxQty: numeric(e.target.value) })
                      }
                      placeholder="10"
                      className="w-full px-3 py-2 border border-[#E0E0E0] rounded-md text-sm 3xl:text-base font-inter text-[#1F1F1F] outline-none transition-colors focus:border-[#0DB5B5]"
                    />
                    <span className="text-sm 3xl:text-base font-inter text-[#6B7280] px-1">
                      =
                    </span>
                    <input
                      type="text"
                      inputMode="decimal"
                      value={band.fat}
                      onChange={(e) =>
                        patchBand(band.id, { fat: numeric(e.target.value) })
                      }
                      placeholder="0.6"
                      className="w-full px-3 py-2 border border-[#E0E0E0] rounded-md text-sm 3xl:text-base font-inter text-[#1F1F1F] outline-none transition-colors focus:border-[#0DB5B5]"
                    />
                    <div className="flex items-center justify-center">
                      <button
                        type="button"
                        onClick={() => requestDeleteBand(band)}
                        className="w-9 h-9 rounded-md bg-[#FDECEC] text-[#E53935] flex items-center justify-center hover:bg-[#FBDADA] transition-colors"
                        aria-label="Remove band"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>

            <button
              type="button"
              onClick={addBandRow}
              className="mt-4 flex items-center gap-1.5 text-[#0DB5B5] text-sm 3xl:text-base font-medium font-inter hover:text-[#0CA0A0] transition-colors"
            >
              <Plus className="w-4 h-4" />
              Add another
            </button>
          </div>
        )}
      </div>

      {/* Card 4: Recipe & ingredients + Recipe instructions */}
      <div className="bg-white rounded-md border border-[#F0F0F0] p-4 sm:p-6 space-y-6">
        <div className="flex items-start justify-between gap-4">
          <div className="flex flex-col">
            <span className="text-sm 3xl:text-base font-semibold font-inter text-[#1F1F1F]">
              Recipe & ingredients
            </span>
            <span className="text-xs 3xl:text-sm font-inter text-[#6B7280] mt-0.5">
              Add ingredients directly below or use the calculator for a guided
              flow. Nutrition updates live.
            </span>
          </div>
          <button
            type="button"
            onClick={onOpenDishCalculator}
            className="flex items-center gap-1.5 px-3 py-1.5 border border-[#0DB5B5] text-[#0DB5B5] rounded-md text-xs 3xl:text-sm font-medium font-inter hover:bg-[#E8F8F8] transition-colors whitespace-nowrap"
          >
            <Calculator className="w-4 h-4" />
            Dish calculator
          </button>
        </div>

        <div className="border border-[#E5E7EB] rounded-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[900px] table-fixed">
              <thead className="bg-[#F9FAFB]">
                <tr className="border-b border-[#E5E7EB]">
                  <th className="text-left py-3 px-4 text-xs 3xl:text-sm font-inter font-medium text-[#1F1F1F] w-[22%]">
                    Name
                  </th>
                  <th className="text-left py-3 pr-3 text-xs 3xl:text-sm font-inter font-medium text-[#1F1F1F] w-[18%]">
                    Units
                  </th>
                  <th className="text-left py-3 pr-3 text-xs 3xl:text-sm font-inter font-medium text-[#1F1F1F] w-[9%]">
                    Quantity
                  </th>
                  <th className="text-center py-3 pr-3 text-xs 3xl:text-sm font-inter font-medium text-[#1F1F1F] w-[6%]">
                    Cal
                  </th>
                  <th className="text-center py-3 pr-3 text-xs 3xl:text-sm font-inter font-medium text-[#1F1F1F] w-[6%]">
                    GL
                  </th>
                  <th className="text-center py-3 pr-3 text-xs 3xl:text-sm font-inter font-medium text-[#1F1F1F] w-[8%]">
                    Net carb
                  </th>
                  <th className="text-center py-3 pr-3 text-xs 3xl:text-sm font-inter font-medium text-[#1F1F1F] w-[5%]">
                    Carb
                  </th>
                  <th className="text-center py-3 pr-3 text-xs 3xl:text-sm font-inter font-medium text-[#1F1F1F] w-[7%]">
                    Protein
                  </th>
                  <th className="text-center py-3 pr-3 text-xs 3xl:text-sm font-inter font-medium text-[#1F1F1F] w-[5%]">
                    Fat
                  </th>
                  <th className="text-center py-3 pr-3 text-xs 3xl:text-sm font-inter font-medium text-[#1F1F1F] w-[11%] whitespace-nowrap">
                    C:P:F
                  </th>
                  <th className="w-[3%] pr-3" />
                </tr>
              </thead>
              <tbody>
                {recipeIngredients.map((row) => {
                  if (confirmDeleteIngredientId === row.id) {
                    return (
                      <tr key={row.id}>
                        <td colSpan={11} className="py-2 px-4">
                          <div className="flex items-center justify-between px-3 py-[6px] bg-[#FFF5F5] border border-[#FFCCC9] rounded-md">
                            <span className="text-sm text-[#1F1F1F] truncate mr-4">
                              Delete{" "}
                              <span className="font-medium">
                                &quot;{row.name || "this ingredient"}&quot;
                              </span>
                              ?
                            </span>
                            <div className="flex items-center gap-2 shrink-0">
                              <Button
                                variant="danger"
                                size="xs"
                                onClick={() => confirmDeleteIngredient(row.id)}
                                className="whitespace-nowrap"
                              >
                                Confirm delete
                              </Button>
                              <button
                                type="button"
                                onClick={() =>
                                  setConfirmDeleteIngredientId(null)
                                }
                                className="text-[#8E8E93] hover:text-[#1F1F1F] transition-colors p-1 rounded border-[0.5px] border-[#1F1F1F]"
                              >
                                <XIcon className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          </div>
                        </td>
                      </tr>
                    );
                  }
                  // Units come from the picked ingredient's measurements
                  // (each carries its own measure_gram, driving the
                  // conversion). Until an ingredient is picked, fall
                  // back to the global units list so the dropdown still
                  // has something to show and stale rows from edit-mode
                  // hydration remain interactable.
                  // Empty until the row has an ingredient — the CustomDropdown
                  // below stays disabled in that state so users can't pick a
                  // unit that isn't tied to a real measurement.
                  const rowUnitOptions =
                    row.measurements && row.measurements.length > 0
                      ? row.measurements.map((m) => ({
                          value: m.unit_type,
                          label: `${m.unit_type} - ${m.measure_gram}gm`,
                        }))
                      : [];
                  return (
                    <tr key={row.id}>
                      <td className="py-3 pl-4 pr-3">
                        <IngredientAutocomplete
                          value={row.name}
                          onSelect={(sug) =>
                            handleIngredientSelect(row.id, sug)
                          }
                          onClear={() => handleIngredientClear(row.id)}
                        />
                      </td>
                      <td className="py-3 pr-3">
                        <CustomDropdown
                          options={rowUnitOptions}
                          value={row.unit}
                          onChange={(v) =>
                            patchIngredientWithNutrition(row.id, { unit: v })
                          }
                          placeholder="Select unit"
                          disabled={rowUnitOptions.length === 0}
                          searchable={false}
                        />
                      </td>
                      <td className="py-3 pr-3">
                        <input
                          type="text"
                          inputMode="decimal"
                          value={row.quantity}
                          onChange={(e) =>
                            patchIngredientWithNutrition(row.id, {
                              quantity: numeric(e.target.value),
                            })
                          }
                          placeholder="Quantity"
                          className="w-full px-3 py-2 border border-[#E0E0E0] rounded-md text-sm 3xl:text-base font-inter text-[#1F1F1F] outline-none transition-colors focus:border-[#0DB5B5]"
                        />
                      </td>
                      <td className="py-3 pr-3 text-center text-sm 3xl:text-base font-inter text-[#1F1F1F]">
                        {row.cal || "00"}
                      </td>
                      <td className="py-3 pr-3 text-center text-sm 3xl:text-base font-inter text-[#1F1F1F]">
                        {row.gl || "00"}
                      </td>
                      <td className="py-3 pr-3 text-center text-sm 3xl:text-base font-inter text-[#1F1F1F]">
                        {row.netCarb || "00"}
                      </td>
                      <td className="py-3 pr-3 text-center text-sm 3xl:text-base font-inter text-[#1F1F1F]">
                        {row.carb || "00"}
                      </td>
                      <td className="py-3 pr-3 text-center text-sm 3xl:text-base font-inter text-[#1F1F1F]">
                        {row.aProt || "00"}
                      </td>
                      <td className="py-3 pr-3 text-center text-sm 3xl:text-base font-inter text-[#1F1F1F]">
                        {row.fats || "00"}
                      </td>
                      <td className="py-3 pr-3 text-center text-sm 3xl:text-base font-inter text-[#1F1F1F] whitespace-nowrap">
                        {row.cpf || "00:00:00"}
                      </td>
                      <td className="py-3 pr-3 text-center">
                        <button
                          type="button"
                          onClick={() => requestDeleteIngredient(row)}
                          disabled={
                            recipeIngredients.length === 1 &&
                            isIngredientRowEmpty(row)
                          }
                          className="text-[#FF3B30] hover:text-[#E63228] transition-colors disabled:text-[#F5B5B0] disabled:cursor-not-allowed inline-flex"
                          aria-label="Remove ingredient"
                          title={
                            recipeIngredients.length === 1 &&
                            isIngredientRowEmpty(row)
                              ? "Nothing to delete"
                              : "Delete ingredient"
                          }
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="px-4 pt-3 pb-10 bg-white">
            <button
              type="button"
              onClick={addIngredientRow}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-[#E8F8F8] border border-[#0DB5B5] text-[#0DB5B5] rounded-md text-xs 3xl:text-sm font-medium font-inter hover:bg-[#D4F0F0] transition-colors"
            >
              <Plus className="w-4 h-4" />
              Add ingredient
            </button>
          </div>
        </div>

        {/* Recipe instructions lives in the same card as the composition
            above — separated by spacing only, not a new white block. */}
        <div className="flex flex-col gap-1.5">
          <label className="text-sm 3xl:text-base font-medium font-inter text-[#1F1F1F]">
            Recipe instructions
          </label>
          <textarea
            value={data.recipeInstructions}
            onChange={(e) => update({ recipeInstructions: e.target.value })}
            placeholder="Step 1. Heat butter and saute onions.."
            rows={4}
            className="w-full px-3 py-2 border border-[#E0E0E0] rounded-md text-sm 3xl:text-base font-inter text-[#1F1F1F] placeholder:text-[#9E9E9E] outline-none transition-colors bg-white focus:border-[#0DB5B5] resize-y"
          />
        </div>
      </div>

      {/* Card 5: Tags */}
      <div className="bg-white rounded-md border border-[#F0F0F0] p-4 sm:p-6">
        <div className="flex flex-col gap-1.5">
          <label className="text-sm 3xl:text-base font-medium font-inter text-[#1F1F1F]">
            Tags
          </label>
          <div
            className={`w-full min-h-[42px] px-3 py-1.5 border rounded-md flex flex-wrap items-center gap-2 bg-white transition-colors ${
              errors.tags
                ? "border-red-400 focus-within:border-red-500"
                : "border-[#E0E0E0] focus-within:border-[#0DB5B5]"
            }`}
            onClick={() => {
              const el = document.getElementById(
                "dish-tags-input",
              ) as HTMLInputElement | null;
              el?.focus();
            }}
          >
            {tags.map((tag) => (
              <span
                key={tag}
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-[#E8F8F8] text-[#1F1F1F] text-xs 3xl:text-sm font-inter"
              >
                {tag}
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    removeTag(tag);
                  }}
                  className="text-[#6B7280] hover:text-[#1F1F1F]"
                  aria-label={`Remove ${tag}`}
                >
                  <XIcon className="w-3 h-3" />
                </button>
              </span>
            ))}
            <input
              id="dish-tags-input"
              type="text"
              value={tagDraft}
              onChange={handleTagChange}
              onKeyDown={handleTagKeyDown}
              onBlur={commitTagDraft}
              placeholder={
                tags.length === 0
                  ? "Add tags (separate with comma or enter)"
                  : ""
              }
              className="flex-1 min-w-[160px] py-1 text-sm 3xl:text-base font-inter text-[#1F1F1F] placeholder:text-[#9E9E9E] outline-none bg-transparent"
            />
          </div>
          {errors.tags && (
            <p className="text-xs 3xl:text-sm text-red-500 font-inter">
              {errors.tags}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default DetailsNutritionStep;
